import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { formatDate } from '../../shared/utility/dateFormatter';
import DATE_FORMATS from '../../shared/constants/dateFormats';
import RMSChip from '../../shared/components/chips/RMSChip';
import { escapeHtml } from '../../shared/utility/barcode';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';

export const isOverdueForReturn = (row) => row?.IsOverdue === true || row?.IsOverdue === 1;
export const isOverdueNotified = (row) =>
  row?.OverdueNotified === true || row?.OverdueNotified === 1;

const OverdueTooltipContent = ({ row }) => (
  <Box className="evidence-overdue-tooltip">
    <Box className="evidence-overdue-tooltip__title">⏰ Overdue for Return</Box>
    <Box>Checked out: {formatDate(row.CheckedOutDate, DATE_FORMATS.DD_MM_YYYY_HH_mm_A)}</Box>
    <Box>Checked out by: {row.CheckedOutBy || '—'}</Box>
    <Box>Checked out to: {row.CustodyTo || '—'}</Box>
    <Box>Reason: {row.CheckoutReason || '—'}</Box>
    <Box>
      Expected return: {formatDate(row.ExpectedReturnDate, DATE_FORMATS.DD_MM_YYYY_HH_mm_A)}
    </Box>
  </Box>
);

const STATUS_CHIP_CLASS = {
  In: 'evidence-status-chip--in',
  Out: 'evidence-status-chip--out',
  Waiting: 'evidence-status-chip--waiting',
  Disposed: 'evidence-status-chip--disposed',
};

export const normalizeStatus = (status) => {
  if (!status) return '';
  const trimmed = String(status).trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

export const getStatusChipClass = (status) =>
  STATUS_CHIP_CLASS[normalizeStatus(status)] || 'evidence-status-chip--default';

const STATUS_PRINT_STYLE = {
  In: { bg: '#dcfce7', text: '#14532d' },
  Out: { bg: '#fef3c7', text: '#78350f' },
  Waiting: { bg: '#e0f2fe', text: '#0c4a6e' },
  Disposed: { bg: '#f1f5f9', text: '#334155' },
};

const DEFAULT_STATUS_PRINT_STYLE = { bg: '#f1f5f9', text: '#334155' };

const getStatusStyle = (status) =>
  STATUS_PRINT_STYLE[normalizeStatus(status)] || DEFAULT_STATUS_PRINT_STYLE;

const renderPrintPill = (label, bg, text, border = 'none') =>
  `<span style="display:inline-block;padding:2px 10px;border-radius:10px;font-size:10px;font-weight:600;background:${bg};color:${text};border:${border};">${escapeHtml(
    label
  )}</span>`;

export const renderPrintStatus = (value) => {
  const label = normalizeStatus(value) || 'Waiting';
  const { bg, text } = getStatusStyle(label);
  return renderPrintPill(label, bg, text);
};

export const renderPrintRequiredAction = (value) =>
  value ? renderPrintPill(value, '#fef3c7', '#78350f') : '—';

export const renderPrintCorrections = (value) =>
  value ? renderPrintPill(value, '#fef2f2', '#dc2626', '1px solid #fecaca') : '—';

export const isDisposalReady = (row) =>
  Array.isArray(row?.violationSet) && row.violationSet.length > 0;

export const EVIDENCE_GRID_ROW_HEIGHT = 56;
export const EVIDENCE_GRID_EXPANDED_ROW_HEIGHT = 84;

export const getEvidenceGridRowHeight = (params) => {
  const row = params?.model ?? params;

  if (isOverdueForReturn(row)) {
    const densityFactor = params?.densityFactor ?? 1;
    return Math.floor(EVIDENCE_GRID_EXPANDED_ROW_HEIGHT * densityFactor);
  }

  return null;
};

const EmptyCell = () => (
  <Box component="span" className="evidence-empty-cell">
    —
  </Box>
);

export const evidneceGridColumns = [
  // Default Columns
  {
    field: 'EvidenceID',
    headerName: 'Evidence ID',
    width: 120,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => {
      const count = Number(params.row?.AttachmentCount) || 0;
      return (
        <Box className="evidence-id-cell">
          <span>{params.value}</span>
          {count > 0 && (
            <Tooltip title={`${count} attachment${count > 1 ? 's' : ''}`}>
              <Box component="span" className="evidence-attachment-badge">
                <AttachFileIcon />
              </Box>
            </Tooltip>
          )}
        </Box>
      );
    },
  },
  {
    field: 'IncidentNumber',
    headerName: 'Incident ID',
    width: 160,
    align: 'left',
    headerAlign: 'left',
    valueGetter: (params) => params.row.incidentReportID || params.row.IncidentNumber,
  },
  {
    field: 'Category',
    headerName: 'Category',
    width: 160,
    align: 'left',
    headerAlign: 'left',
  },
  {
    field: 'EvidenceType',
    headerName: 'Evidence Type',
    width: 160,
    align: 'left',
    headerAlign: 'left',
  },
  {
    field: 'Description',
    headerName: 'Description',
    width: 260,
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => {
      const hasAlert = params.row?.HasAlertNote === true;
      const alertText = params.row?.AlertNoteText;
      return (
        <Box className="evidence-description-cell">
          {hasAlert && (
            <Tooltip title={alertText || 'Alert note on this item'}>
              <Box component="span" className="evidence-alert-icon">
                <WarningAmberIcon />
              </Box>
            </Tooltip>
          )}
          <span className="evidence-description-text">{params.value}</span>
        </Box>
      );
    },
  },
  {
    field: 'Quantity',
    headerName: 'Qty',
    width: 120,
    align: 'left',
    headerAlign: 'left',
  },
  {
    field: 'Status',
    headerName: 'Status',
    width: 300,
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => {
      const label = normalizeStatus(params.value) || 'Waiting';
      const disposalReady = isDisposalReady(params.row);
      const overdue = isOverdueForReturn(params.row);

      return (
        <Box className="evidence-status-cell">
          <RMSChip
            label={label}
            size="small"
            className={`evidence-chip evidence-chip--status ${getStatusChipClass(label)}`}
          />
          {disposalReady && (
            <RMSChip
              icon={<WarningAmberIcon />}
              label="Disposal"
              size="small"
              className="evidence-chip evidence-chip--disposal"
            />
          )}
          {overdue && (
            <Box className="evidence-overdue-wrapper">
              {isOverdueNotified(params.row) && (
                <Tooltip title="Overdue notification sent to officer" arrow placement="top">
                  <CheckCircleIcon className="evidence-overdue-notified-icon" />
                </Tooltip>
              )}
              <Tooltip
                title={<OverdueTooltipContent row={params.row} />}
                arrow
                placement="bottom-start">
                <span>
                  <RMSChip
                    label={`⏰ Overdue${
                      params.row?.ExpectedReturnDate
                        ? ` — ${formatDate(
                            params.row.ExpectedReturnDate,
                            DATE_FORMATS.MM_DD_YYYY_HH_mm_A_COMMA
                          )}`
                        : ''
                    }`}
                    size="small"
                    shape="square"
                    className="evidence-chip evidence-chip--overdue"
                  />
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      );
    },
  },
  {
    field: 'CurrentLocation',
    headerName: 'Current Location',
    width: 180,
    align: 'left',
    headerAlign: 'left',
  },
  {
    field: 'SubmittedBy',
    headerName: 'Submitted By',
    width: 180,
    align: 'left',
    headerAlign: 'left',
  },
  {
    field: 'SubmittedAt',
    headerName: 'Submitted At',
    width: 180,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (params) => formatDate(params.value, DATE_FORMATS.YYYY_MM_DD),
  },
  {
    field: 'RequiredAction',
    headerName: 'Required Action',
    width: 180,
    align: 'left',
    headerAlign: 'left',
    renderCell: (params) => {
      const value = params.value;
      if (!value) return <EmptyCell />;

      return (
        <RMSChip
          label={value}
          size="small"
          className="evidence-chip evidence-chip--required-action"
        />
      );
    },
  },
  {
    field: 'OpenCorrectionCount',
    headerName: 'Corrections',
    width: 150,
    align: 'left',
    headerAlign: 'left',
    type: 'number',
    renderCell: (params) => {
      const count = Number(params.value) || 0;
      if (count <= 0) return <EmptyCell />;

      return (
        <Chip
          icon={<FiberManualRecordIcon />}
          label={`${count} open`}
          size="small"
          className="evidence-chip evidence-chip--corrections"
        />
      );
    },
  },
  {
    field: 'ExpectedReturnDate',
    headerName: 'Exp. Return',
    width: 180,
    align: 'left',
    headerAlign: 'left',
    valueFormatter: (params) => formatDate(params.value, DATE_FORMATS.YYYY_MM_DD),
  },

  // Hidden by Default
  {
    field: 'TagNumber',
    headerName: 'Tag Number',
    width: 150,
    align: 'left',
    headerAlign: 'left',
    hide: true,
  },
  {
    field: 'CollectedBy',
    headerName: 'Collected By',
    width: 180,
    align: 'left',
    headerAlign: 'left',
    hide: true,
  },
  {
    field: 'DateTimeCollected',
    headerName: 'Date Collected',
    width: 180,
    align: 'left',
    headerAlign: 'left',
    hide: true,
    valueFormatter: (params) => formatDate(params.value, DATE_FORMATS.YYYY_MM_DD),
  },
  {
    field: 'LocationCollected',
    headerName: 'Location Collected',
    width: 180,
    align: 'left',
    headerAlign: 'left',
    hide: true,
  },
  {
    field: 'TemporaryLocation',
    headerName: 'Temp Location',
    width: 180,
    align: 'left',
    headerAlign: 'left',
    hide: true,
  },
  {
    field: 'Condition',
    headerName: 'Condition',
    width: 140,
    align: 'left',
    headerAlign: 'left',
    hide: true,
  },
  {
    field: 'Make',
    headerName: 'Make',
    width: 120,
    align: 'left',
    headerAlign: 'left',
    hide: true,
  },
  {
    field: 'Model',
    headerName: 'Model',
    width: 120,
    align: 'left',
    headerAlign: 'left',
    hide: true,
  },
  {
    field: 'SerialNumber',
    headerName: 'Serial Number',
    width: 180,
    align: 'left',
    headerAlign: 'left',
    hide: true,
  },
  {
    field: 'VIN',
    headerName: 'VIN',
    width: 180,
    align: 'left',
    headerAlign: 'left',
    hide: true,
  },
  {
    field: 'LicenseNumber',
    headerName: 'Plate / License',
    width: 180,
    align: 'left',
    headerAlign: 'left',
    hide: true,
  },
  {
    field: 'SubstanceType',
    headerName: 'Substance Category',
    width: 180,
    align: 'left',
    headerAlign: 'left',
    hide: true,
  },
  {
    field: 'SubstanceClass',
    headerName: 'Drug Type',
    width: 180,
    align: 'left',
    headerAlign: 'left',
    hide: true,
  },
  {
    field: 'Notes',
    headerName: 'Notes',
    width: 250,
    align: 'left',
    headerAlign: 'left',
    hide: true,
  },
  {
    field: 'DaysInEvidence',
    headerName: 'Days in Evidence',
    width: 150,
    align: 'left',
    headerAlign: 'left',
    hide: true,
  },
];

export const EVIDENCE_LIST_PRINT_COLUMNS = [
  { field: 'EvidenceID', label: 'Barcode', type: 'barcode', scale: 0.5 },
  { field: 'EvidenceID', label: 'Evidence ID' },
  { field: 'IncidentNumber', label: 'Incident Number' },
  { field: 'Category', label: 'Category' },
  { field: 'EvidenceType', label: 'Type' },
  { field: 'Description', label: 'Description' },
  { field: 'Quantity', label: 'Qty' },
  { field: 'Status', label: 'Status', render: renderPrintStatus },
  { field: 'CurrentLocation', label: 'Location' },
  { field: 'SubmittedBy', label: 'Submitted By' },
  { field: 'SubmittedAt', label: 'Submitted' },
  { field: 'RequiredAction', label: 'Required Action', render: renderPrintRequiredAction },
  { field: 'OpenCorrectionCount', label: 'Corrections', render: renderPrintCorrections },
  { field: 'ExpectedReturnDate', label: 'Exp. Return' },
  { field: 'TagNumber', label: 'Tag Number' },
  { field: 'CollectedBy', label: 'Collected By' },
  { field: 'DateTimeCollected', label: 'Date Collected' },
  { field: 'LocationCollected', label: 'Location Collected' },
];

export const STATUS_CLASS_NAMES = {
  In: 'statusIn',
  Out: 'statusOut',
  Waiting: 'statusWaiting',
  Disposed: 'statusDisposed',
};

export const CORRECTION_STATUSES = {
  any: 'any',
  open: 'open',
  resolved: 'resolved',
  inProgress: 'in progress',
  closed: 'closed',
};
